/**
 * feat-259 AC13 / AT14 — the confirmation shown when a lesson or drill is
 * switched ON while it contains an empty item.
 *
 * A REAL DIALOG, not `window.confirm`: AT14 requires a LIST, and type badges,
 * per-drill grouping and per-item reasons do not survive a native confirm's
 * single string. Delete keeps its `window.confirm` (spec decision #3) —
 * different question, different stakes, different surface.
 *
 * Design authority: `design/publish-guard.html`.
 */

import React, { useEffect, useRef } from 'react';
import {
  EMPTY_ITEM_BADGE,
  PUBLISH_GUARD_COPY,
  PublishGuardReport,
} from './publishGuard';

export interface PublishGuardDialogProps {
  /** The lesson's or drill's own title — the thing being identified. */
  containerTitle: string;
  report: PublishGuardReport;
  onCancel: () => void;
  onConfirm: () => void;
}

export const PublishGuardDialog: React.FC<PublishGuardDialogProps> = ({
  containerTitle,
  report,
  onCancel,
  onConfirm,
}) => {
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    // Initial focus lands on Cancel, NOT on "Turn on anyway": a stray Enter
    // should not publish incomplete content to learners.
    cancelRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const grouped = report.groups.length > 1;

  const row = (item: PublishGuardReport['groups'][number]['items'][number]) => {
    const badge = EMPTY_ITEM_BADGE[item.kind];
    return (
      <div key={item.id} className="flex items-center gap-2.5 px-3 py-2 text-sm">
        <span className={`text-[10px] font-bold rounded-md px-1.5 py-0.5 shrink-0 ${badge.className}`}>
          {badge.label}
        </span>
        {/* Item labels are GENERATED and short, so truncate here is a cheap
            defence that will never actually fire. */}
        <span className="font-semibold min-w-0 truncate">{item.label}</span>
        <span className="text-xs text-inkMuted ml-auto shrink-0">{item.reason}</span>
      </div>
    );
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
      // Dismissing means Off by EVERY route: Cancel, Esc, and an overlay click
      // all leave the toggle Off and write nothing.
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
      data-testid="publish-guard-overlay"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="publish-guard-title"
        className="bg-white rounded-2xl border border-softBorder shadow-xl w-full max-w-md p-6"
      >
        {/* The container title is user-authored and unbounded. It is the thing
            being identified, so it wraps and is never clipped. */}
        <h3 id="publish-guard-title" className="font-display text-lg font-semibold break-words">
          {PUBLISH_GUARD_COPY.title(containerTitle)}
        </h3>
        <p className="text-sm text-inkMuted mt-1.5 leading-relaxed">
          <strong className="text-ink">
            {PUBLISH_GUARD_COPY.lede(report.emptyCount, report.totalCount, report.drillsWithEmpties)}
          </strong>{' '}
          {PUBLISH_GUARD_COPY.consequence(report.emptyCount)}
        </p>

        <div className="mt-3 border border-softBorder rounded-xl overflow-hidden">
          {/* Scrolls; it NEVER truncates to "+4 more". The count in the lede is
              the number the editor is deciding on, and a list that disagrees
              with its own count is worse than no list. */}
          <div className="max-h-[13rem] overflow-y-auto divide-y divide-[rgba(0,0,0,0.06)]">
            {report.groups.map((g) => (
              <React.Fragment key={g.drillId}>
                {grouped && (
                  <div className="px-3 py-1.5 bg-cream text-[11px] font-bold uppercase tracking-wide text-inkMuted">
                    {g.drillTitle || '(untitled)'}
                  </div>
                )}
                {g.items.map(row)}
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 mt-5">
          <button
            ref={cancelRef}
            type="button"
            onClick={onCancel}
            className="text-inkMuted text-sm font-semibold px-3 py-2 rounded-full hover:bg-gray-100"
          >
            {PUBLISH_GUARD_COPY.cancel}
          </button>
          {/* Not destructive-red. Publishing a thin lesson is a reversible
              authoring choice, not a deletion — `coral` is the app's primary CTA
              and `danger-*` is reserved for the irreversible. */}
          <button
            type="button"
            onClick={onConfirm}
            className="bg-coral text-white font-display font-semibold rounded-full px-5 py-2 text-sm hover:brightness-95 shrink-0"
          >
            {PUBLISH_GUARD_COPY.confirm}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PublishGuardDialog;
